import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Assessment as ResultsIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { apiFetch } from '../../config/api';
import { StatusChip } from '../ExecutionManager/ExecutionListComponents/utils';
import { debugWarn, debugError } from '../../utils/debugLog';
import { createIOSButtonStyles } from '../../theme/iosButtonStyles';
import { useTheme } from '@mui/material/styles';
import { SPACING } from '../../theme';
import { adjustOpacity } from '../../theme/utils';
import ExperimentDetailsDialog from './components/ExperimentDetailsDialog';
import { useExperiments } from './hooks/useExperiments';
import PaginationBar from '../common/PaginationBar';

const ExperimentResults = () => {
  const theme = useTheme();
  const iosButtonStyles = createIOSButtonStyles(theme);
  const { getExperiment } = useExperiments();

  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedExperiment, setSelectedExperiment] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [experimentLoading, setExperimentLoading] = useState(false);

  const loadExecutions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters with pagination
      const queryParams = new URLSearchParams();
      queryParams.append('is_experiment', 'true');
      queryParams.append('page', (page + 1).toString()); // API uses 1-based pagination
      queryParams.append('page_size', rowsPerPage.toString());

      // Load experiment executions only
      // apiFetch returns parsed JSON data directly, not a Response object
      const data = await apiFetch(
        `/api/v1/templates/executions/?${queryParams.toString()}`
      );

      // Handle response structure - apiFetch already parsed the JSON
      if (data && data.executions) {
        setExecutions(data.executions || []);
        setTotalCount(data.total_count || 0);
      } else if (Array.isArray(data)) {
        // Fallback: if API returns array directly
        setExecutions(data);
        setTotalCount(data.length);
      } else {
        debugWarn('Unexpected response format from executions API:', data);
        setExecutions([]);
        setTotalCount(0);
      }
    } catch (err) {
      // Error loading experiment executions
      debugError('Failed to load experiment executions:', err);
      setError('Failed to load experiment executions');
      setExecutions([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  // Load experiment executions only
  useEffect(() => {
    loadExecutions();
  }, [loadExecutions]);

  const getExperimentName = experimentId => {
    return `Experiment ${experimentId}`;
  };

  const getTreatmentName = (treatmentId, experimentId, treatmentName) => {
    // Use the treatment_name from the API response if available
    if (treatmentName) {
      return treatmentName;
    }
    return `Treatment ${treatmentId}`;
  };

  const filteredExecutions = executions.filter(execution => {
    if (
      filterStatus !== 'all' &&
      execution.success !== (filterStatus === 'success')
    ) {
      return false;
    }
    return true;
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when page size changes
  };

  const formatDate = dateString => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = executionTime => {
    return `${(executionTime || 0).toFixed(2)}s`;
  };

  const handleViewExperimentDetails = async experimentId => {
    if (!experimentId) return;

    try {
      setExperimentLoading(true);
      const experiment = await getExperiment(experimentId);
      if (experiment) {
        setSelectedExperiment(experiment);
        setDetailsDialogOpen(true);
      } else {
        debugWarn('Experiment not found:', experimentId);
      }
    } catch (err) {
      debugError('Failed to load experiment details:', err);
    } finally {
      setExperimentLoading(false);
    }
  };

  const handleStatusChange = async (experimentId, action) => {
    // Reload executions after status change
    await loadExecutions();
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <ResultsIcon sx={{ mr: 2, color: 'primary.main' }} />
          Experiment Results
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Analyze performance and outcomes of your A/B testing experiments
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards - Modern, Sleek Design */}
      <Box sx={{ width: '100%', px: 14, mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-evenly',
            alignItems: 'stretch',
            gap: 14,
          }}
        >
          <Box sx={{ flex: 1, display: 'flex', minWidth: 0 }}>
            <Card
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: theme.palette.primary.contrastText,
                borderRadius: 2,
                boxShadow: `0 4px 12px ${adjustOpacity(theme.palette.primary.main, 0.2)}`,
                transition: 'all 0.3s ease',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 20px ${adjustOpacity(theme.palette.primary.main, 0.3)}`,
                },
              }}
            >
              <CardContent
                sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                  }}
                >
                  <TrendingUpIcon
                    sx={{
                      mr: 1,
                      fontSize: '1.5rem',
                      color: theme.palette.primary.contrastText,
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.primary.contrastText,
                    }}
                  >
                    Total Executions
                  </Typography>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.contrastText,
                    flex: 1,
                    textAlign: 'center',
                  }}
                >
                  {totalCount}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: 1, display: 'flex', minWidth: 0 }}>
            <Card
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                color: theme.palette.success.contrastText,
                borderRadius: 2,
                boxShadow: `0 4px 12px ${adjustOpacity(theme.palette.success.main, 0.2)}`,
                transition: 'all 0.3s ease',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 20px ${adjustOpacity(theme.palette.success.main, 0.3)}`,
                },
              }}
            >
              <CardContent
                sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                  }}
                >
                  <CheckCircleIcon
                    sx={{
                      mr: 1,
                      fontSize: '1.5rem',
                      color: theme.palette.success.contrastText,
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.success.contrastText,
                    }}
                  >
                    Success Rate
                  </Typography>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.success.contrastText,
                    flex: 1,
                    textAlign: 'center',
                  }}
                >
                  {executions.length > 0
                    ? `${((executions.filter(e => e.success).length / executions.length) * 100).toFixed(1)}%`
                    : totalCount > 0
                      ? 'Calculating...'
                      : '0%'}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: 1, display: 'flex', minWidth: 0 }}>
            <Card
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
                color: theme.palette.info.contrastText,
                borderRadius: 2,
                boxShadow: `0 4px 12px ${adjustOpacity(theme.palette.info.main, 0.2)}`,
                transition: 'all 0.3s ease',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 20px ${adjustOpacity(theme.palette.info.main, 0.3)}`,
                },
              }}
            >
              <CardContent
                sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                  }}
                >
                  <TrendingUpIcon
                    sx={{
                      mr: 1,
                      fontSize: '1.5rem',
                      color: theme.palette.info.contrastText,
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.info.contrastText,
                    }}
                  >
                    Avg Response Time
                  </Typography>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.info.contrastText,
                    flex: 1,
                    textAlign: 'center',
                  }}
                >
                  {executions.length > 0
                    ? `${(executions.reduce((sum, e) => sum + (e.execution_time || 0), 0) / executions.length).toFixed(2)}s`
                    : '0s'}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>

      {/* Filters - Modern Design */}
      <Paper
        sx={{
          p: SPACING.lg,
          mb: 3,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <FilterIcon
              sx={{ color: theme.palette.primary.main, fontSize: '1.5rem' }}
            />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Filters
            </Typography>
          </Box>
          <FormControl sx={{ flex: 1, minWidth: 200 }}>
            <InputLabel id="status-select-label">Status</InputLabel>
            <Select
              labelId="status-select-label"
              id="status-select"
              value={filterStatus}
              label="Status"
              onChange={e => setFilterStatus(e.target.value)}
              sx={{
                borderRadius: 1,
                height: '40px',
                '& .MuiSelect-select': {
                  height: '40px',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.divider,
                },
              }}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="success">Successful</MenuItem>
              <MenuItem value="error">Failed</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadExecutions}
            sx={{
              // iOS-style secondary button
              ...iosButtonStyles.secondary,
              textTransform: 'none',
              fontFamily:
                "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
              height: '40px',
              minHeight: '40px',
              maxHeight: '40px',
              minWidth: '120px',
            }}
          >
            Refresh
          </Button>
        </Box>
      </Paper>

      {/* Results Table - Modern Design */}
      <Paper
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: theme.palette.background.default,
                  '& .MuiTableCell-head': {
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    color: theme.palette.text.primary,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                  },
                }}
              >
                <TableCell>Experiment</TableCell>
                <TableCell>Treatment</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Response Time</TableCell>
                <TableCell>Cost</TableCell>
                <TableCell>Executed At</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredExecutions.map((execution, index) => (
                <TableRow
                  key={execution.id}
                  hover
                  sx={{
                    '&:hover': {
                      backgroundColor:
                        theme.palette.action.hover ||
                        theme.palette.background.default,
                    },
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      padding: SPACING.md,
                    },
                  }}
                >
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ color: theme.palette.text.primary }}
                    >
                      {getExperimentName(execution.experiment_id)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      ID: {execution.experiment_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getTreatmentName(
                        execution.treatment_id,
                        execution.experiment_id,
                        execution.treatment_name
                      )}
                      size="small"
                      sx={{
                        width: 80,
                        height: 24,
                        justifyContent: 'center',
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        '& .MuiChip-label': {
                          paddingLeft: '8px',
                          paddingRight: '8px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                        '&:hover': {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      sx={{
                        color: theme.palette.text.primary,
                        wordBreak: 'break-all',
                        overflowWrap: 'break-word',
                      }}
                    >
                      {execution.template_id || 'Unknown Template'}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Version: {execution.template_version}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusChip success={execution.success} />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.primary,
                        fontWeight: 500,
                      }}
                    >
                      {formatDuration(execution.execution_time)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.primary,
                        fontWeight: 500,
                      }}
                    >
                      {execution.cost ? `$${execution.cost.toFixed(4)}` : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {formatDate(execution.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {execution.created_by || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Experiment Details">
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleViewExperimentDetails(execution.experiment_id)
                        }
                        disabled={experimentLoading || !execution.experiment_id}
                        sx={{
                          color: theme.palette.primary.main,
                          '&:hover': {
                            backgroundColor: adjustOpacity(
                              theme.palette.primary.main,
                              0.1
                            ),
                          },
                          '&:disabled': {
                            color: theme.palette.text.secondary,
                          },
                        }}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ p: 2 }}>
          <PaginationBar
            currentPage={page + 1} // Convert 0-based to 1-based
            totalPages={Math.ceil(totalCount / rowsPerPage) || 1}
            totalCount={totalCount}
            pageSize={rowsPerPage}
            onPrev={() => handleChangePage(null, page - 1)}
            onNext={() => handleChangePage(null, page + 1)}
            onPageClick={newPage => handleChangePage(null, newPage - 1)} // Convert 1-based to 0-based
            onPageSizeChange={newSize =>
              handleChangeRowsPerPage({ target: { value: newSize } })
            }
          />
        </Box>
      </Paper>

      {filteredExecutions.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No experiment results found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filterStatus !== 'all'
              ? 'Try adjusting your filters'
              : 'Execute some experiments to see results here'}
          </Typography>
        </Box>
      )}

      {/* Experiment Details Dialog */}
      <ExperimentDetailsDialog
        open={detailsDialogOpen}
        onClose={() => {
          setDetailsDialogOpen(false);
          setSelectedExperiment(null);
        }}
        experiment={selectedExperiment}
        onStatusChange={handleStatusChange}
      />
    </Container>
  );
};

export default ExperimentResults;
